<template>
	<v-app>
		<v-navigation-drawer
			v-model="drawer"
			app
			clipped
			floating
			:width="$vuetify.breakpoint.smAndDown ? '310' : '280'"
			class="background-1"
			:permanent="$vuetify.breakpoint.mdAndUp"
			:temporary="$vuetify.breakpoint.smAndDown"
		>
			<v-list dense class="pt-2">
				<v-list-item class="px-4 mb-2">
					<v-list-item-avatar>
						<v-avatar v-if="portalLogo" :size="40" class="custom-avatar" :style="{'background-image': 'url(' + portalLogo + ')'}"></v-avatar>
						<v-icon v-else large color="primary">mdi-shield-account</v-icon>
					</v-list-item-avatar>
					<v-list-item-content>
						<v-list-item-title class="font-weight-bold">{{ portalName }}</v-list-item-title>
						<v-list-item-subtitle v-if="user">{{ user.name }}</v-list-item-subtitle>
					</v-list-item-content>
				</v-list-item>

				<v-divider class="mb-2"></v-divider>

				<!-- Company switcher (solo multi-company) -->
				<v-list-item v-if="isMultiCompany" class="px-4 mb-2">
					<v-select
						:value="currentCompanyId"
						:items="companies"
						item-text="name"
						item-value="id"
						dense
						outlined
						hide-details
						prepend-icon="mdi-domain"
						@change="switchCompany"
						:menu-props="{'offset-y': true}"
					></v-select>
				</v-list-item>
				<v-divider v-if="isMultiCompany" class="mb-2"></v-divider>

				<v-list-item
					v-for="item in menuItems"
					:key="item.route"
					:to="item.route"
					:exact="item.exact !== false"
					active-class="primary--text"
					ripple
				>
					<v-list-item-action>
						<v-icon>{{ item.icon }}</v-icon>
					</v-list-item-action>
					<v-list-item-content>
						<v-list-item-title>{{ item.title }}</v-list-item-title>
					</v-list-item-content>
					<v-list-item-action v-if="item.badge">
						<v-badge :content="item.badge" color="red" inline></v-badge>
					</v-list-item-action>
				</v-list-item>

				<v-divider class="my-2"></v-divider>

				<v-list-item ripple @click="logout">
					<v-list-item-action>
						<v-icon color="red">mdi-exit-to-app</v-icon>
					</v-list-item-action>
					<v-list-item-content>
						<v-list-item-title class="red--text">Esci</v-list-item-title>
					</v-list-item-content>
				</v-list-item>
			</v-list>
		</v-navigation-drawer>

		<v-app-bar app clipped-left clipped-right color="primary" dark>
			<v-btn icon @click="drawer = !drawer" v-if="$vuetify.breakpoint.smAndDown">
				<v-icon>mdi-menu</v-icon>
			</v-btn>
			<v-toolbar-title class="font-weight-bold pl-4">
				{{ currentTitle }}
			</v-toolbar-title>
			<v-spacer></v-spacer>
			<v-btn icon :to="{ name: 'portal-notifications' }">
				<v-badge :content="unreadCount" :value="unreadCount > 0" color="red" overlap>
					<v-icon>mdi-bell</v-icon>
				</v-badge>
			</v-btn>
		</v-app-bar>

		<v-main>
			<router-view :key="currentCompanyId"></router-view>
		</v-main>

		<v-snackbar
			:timeout="snackbar.timeout"
			:color="snackbar.color"
			v-model="snackbar.value"
		>
			{{ snackbar.text }}
			<template v-slot:action="{ attrs }">
				<v-btn dark icon v-bind="attrs" @click="snackbar.value = false">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</template>
		</v-snackbar>
	</v-app>
</template>

<script>
export default {
	name: 'PortalLayout',
	data() {
		return {
			drawer: this.$vuetify.breakpoint.mdAndUp,
			portalLogo: null,
			portalName: 'Area Privata',
			projectsCount: 0,
			snackbar: {
				value: false,
				text: '',
				color: '',
				timeout: 4000
			}
		}
	},
	computed: {
		user() {
			return this.$store.getters.portalUser
		},
		unreadCount() {
			return this.$store.getters.portalUnreadNotifications
		},
		currentTitle() {
			return this.$route.meta.title || this.portalName
		},
		isMultiCompany() {
			return this.$store.getters.portalIsMultiCompany
		},
		companies() {
			return this.$store.getters.portalCompanies
		},
		currentCompanyId() {
			return this.$store.getters.portalCurrentCompanyId
		},
		menuItems() {
			const items = [
				{ title: 'Dashboard', icon: 'mdi-view-dashboard', route: '/portal' },
				{ title: 'Documenti', icon: 'mdi-folder-multiple', route: '/portal/documents', exact: false }
			]
			if (this.projectsCount > 0) {
				items.push({ title: 'Progetti', icon: 'mdi-clipboard-text-multiple', route: '/portal/projects', exact: false })
			}
			items.push(
				{
					title: 'Notifiche',
					icon: 'mdi-bell',
					route: '/portal/notifications',
					badge: this.unreadCount > 0 ? String(this.unreadCount) : null
				},
				{ title: 'Profilo', icon: 'mdi-account', route: '/portal/profile' }
			)
			return items
		}
	},
	created() {
		// Se il cambio password è obbligatorio, redireziona subito
		if (this.$store.getters.portalForcePasswordChange) {
			this.$router.replace('/portal/change-password')
			return
		}
		this.$store.dispatch('PORTAL_RESTORE_SESSION')
		this.$store.dispatch('PORTAL_FETCH_PROFILE')
		this.$store.dispatch('PORTAL_FETCH_UNREAD')
		this.loadBranding()
		this.loadProjectsCount()
		this.pollNotifications()
	},
	beforeDestroy() {
		if (this._notifInterval) clearInterval(this._notifInterval)
	},
	methods: {
		async loadBranding() {
			// Usa cache localStorage come fallback immediato
			const cached = localStorage.getItem('portal-branding')
			if (cached) {
				try {
					const d = JSON.parse(cached)
					if (d.portal_color) {
						this.$vuetify.theme.themes.light.primary = d.portal_color
						this.$vuetify.theme.themes.dark.primary = d.portal_color
					}
					if (d.portal_logo) this.portalLogo = d.portal_logo
					if (d.portal_company_name) this.portalName = d.portal_company_name
				} catch (e) { /* cache corrotta, ignora */ }
			}
			try {
				const resp = await this.axios.get('portal/portal-branding')
				if (resp.data.success) {
					const d = resp.data.data
					localStorage.setItem('portal-branding', JSON.stringify(d))
					if (d.portal_color) {
						this.$vuetify.theme.themes.light.primary = d.portal_color
						this.$vuetify.theme.themes.dark.primary = d.portal_color
					}
					if (d.portal_logo) {
						this.portalLogo = d.portal_logo
					}
					if (d.portal_company_name) {
						this.portalName = d.portal_company_name
					}
				}
			} catch (e) {
				// Branding non disponibile, usa cache o defaults
			}
		},
		async loadProjectsCount() {
			try {
				const resp = await this.axios.get('portal/projects')
				if (resp.data.success) this.projectsCount = (resp.data.data || []).length
			} catch (e) {
				// silenzioso: la voce "Progetti" resta nascosta
			}
		},
		pollNotifications() {
			this._notifInterval = setInterval(() => {
				this.$store.dispatch('PORTAL_FETCH_UNREAD')
			}, 60000)
		},
		async switchCompany(companyId) {
			await this.$store.dispatch('PORTAL_SELECT_COMPANY', companyId)
			this.$store.dispatch('PORTAL_FETCH_PROFILE')
			this.$store.dispatch('PORTAL_FETCH_UNREAD')
			// I progetti dipendono dall'azienda: ricalcola la voce di menu
			this.projectsCount = 0
			this.loadProjectsCount()
			if (this.$route.path !== '/portal') {
				this.$router.push('/portal')
			}
		},
		async logout() {
			await this.$store.dispatch('PORTAL_LOGOUT')
		},
		showSnackbar(text, color = 'success') {
			this.snackbar = { value: true, text, color, timeout: 4000 }
		}
	}
}
</script>
